import { Moon, Sun } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { ThemeMode } from '../../../../utils/darkMode';

const OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** Appearance row: the theme tri-state (System / Light / Dark) control (CH-11). */
export function ThemeModeSection() {
  const themeMode = useAppStore((state) => state.themeMode);
  const isDarkMode = useAppStore((state) => state.isDarkMode);
  const setThemeMode = useAppStore((state) => state.setThemeMode);
  const { track } = useAnalytics();

  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
      <div className="flex items-center gap-3">
        {isDarkMode ? (
          <Moon className="h-5 w-5 text-muted-foreground" />
        ) : (
          <Sun className="h-5 w-5 text-muted-foreground" />
        )}
        <div className="grid gap-0.5">
          <Label className="cursor-default">Theme</Label>
          <div className="text-xs text-muted-foreground">
            Light, dark, or follow your system setting.
          </div>
        </div>
      </div>
      <RadioGroup
        value={themeMode}
        onValueChange={(value) => {
          setThemeMode(value as ThemeMode);
          // Carries over the instrumentation `main` added to the boolean Dark
          // Mode switch this tri-state replaced. A mode switch reports
          // `{ mode }`, not `{ enabled }`, per the CLAUDE.md analytics rules.
          track('feature:theme_mode_changed', { mode: value });
        }}
        className="ml-6 flex items-center gap-4"
        data-testid="theme-mode-radio-group"
      >
        {OPTIONS.map((opt) => (
          <div key={opt.value} className="flex items-center gap-1.5">
            <RadioGroupItem
              id={`theme-mode-${opt.value}`}
              value={opt.value}
              data-testid={`theme-mode-${opt.value}`}
            />
            <Label htmlFor={`theme-mode-${opt.value}`} className="cursor-pointer text-sm font-normal">
              {opt.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}
